import React from 'react';

// Question Answer Interface
export interface QuestionAnswer {
  answer?: string;
  comments?: string;
  details?: string;
  actualStartTime?: string;
  invalidBallotCount?: number;
  explanation?: string;
  // New gender breakdown fields
  genderMale?: number;
  genderFemale?: number;
}

// Conditional Logic Interface
export interface QuestionConditional {
  dependsOn: string;
  showWhen: string;
}

// Question Interface
export interface QuestionInterface {
  id: string;
  question: string;
  type: string;
  required: boolean;
  conditional?: QuestionConditional;
}

// Base Question Props
interface BaseQuestionProps {
  id: string;
  question: string;
  value: QuestionAnswer;
  onChange: (value: QuestionAnswer) => void;
  required?: boolean;
  error?: string;
}

// Yes/No Question
export const YesNoQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Comments
export const YesNoCommentsQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    <textarea
      placeholder="Additional comments (optional)"
      className="input w-full h-20 resize-none"
      value={value.comments || ''}
      onChange={(e) => onChange({ ...value, comments: e.target.value })}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No/Partially
export const YesNoPartiallyQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4">
      {['Yes', 'No', 'Partially'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No/Not Applicable
export const YesNoNAQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4">
      {['Yes', 'No', 'Not Applicable'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Details (required if Yes)
export const YesNoDetailsQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'Yes' && (
      <textarea
        placeholder="Please provide details (required)"
        className="input w-full h-20 resize-none"
        value={value.details || ''}
        onChange={(e) => onChange({ ...value, details: e.target.value })}
        required
      />
    )}
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Time
export const YesNoTimeQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'No' && (
      <div>
        <label className="label text-sm">Actual start time:</label>
        <input
          type="time"
          className="input w-32"
          value={value.actualStartTime || ''}
          onChange={(e) => onChange({ ...value, actualStartTime: e.target.value })}
        />
      </div>
    )}
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Always/Sometimes/Never
export const AlwaysSometimesNeverQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4">
      {['Always', 'Sometimes', 'Never'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Rating Question
export const RatingQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4">
      {['Excellent', 'Good', 'Fair', 'Poor'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Count
export const YesNoCountQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'Yes' && (
      <div>
        <label className="label text-sm">Number of invalid ballots:</label>
        <input
          type="number"
          className="input w-24"
          min="0"
          value={value.invalidBallotCount || ''}
          onChange={(e) => onChange({ ...value, invalidBallotCount: parseInt(e.target.value) || 0 })}
        />
      </div>
    )}
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Explanation
export const YesNoExplanationQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    <textarea
      placeholder="Please provide an explanation (required)"
      className="input w-full h-20 resize-none"
      value={value.explanation || ''}
      onChange={(e) => onChange({ ...value, explanation: e.target.value })}
      required
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Explanation for No
export const YesNoExplanationIfNoQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'No' && (
      <textarea
        placeholder="Please explain why (required)"
        className="input w-full h-20 resize-none"
        value={value.details || ''}
        onChange={(e) => onChange({ ...value, details: e.target.value })}
        required
      />
    )}
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Yes/No with Multiple Checkbox Reasons for Late Opening (Question 1a)
export const YesNoLateOpeningReasonsQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => {
  const lateOpeningReasons = [
    'No materials',
    'Logistics',
    'Weather',
    'No polling staff',
    'Delayed security',
    'Other'
  ];

  // Parse selected reasons from details field (stored as JSON array)
  const getSelectedReasons = (): string[] => {
    try {
      return value.details ? JSON.parse(value.details) : [];
    } catch {
      return [];
    }
  };

  // Handle checkbox changes
  const handleReasonChange = (reason: string, isChecked: boolean) => {
    const currentReasons = getSelectedReasons();
    let newReasons: string[];

    if (isChecked) {
      newReasons = [...currentReasons, reason];
    } else {
      newReasons = currentReasons.filter(r => r !== reason);
      // Clear "Other" explanation if "Other" is unchecked
      if (reason === 'Other') {
        onChange({ ...value, details: JSON.stringify(newReasons), comments: '' });
        return;
      }
    }

    onChange({ ...value, details: JSON.stringify(newReasons) });
  };

  const selectedReasons = getSelectedReasons();
  const isOtherSelected = selectedReasons.includes('Other');

  return (
    <div className="mb-4">
      <label className="label">
        {question} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex space-x-4 mb-2">
        {['Yes', 'No'].map((option) => (
          <label key={option} className="flex items-center">
            <input
              type="radio"
              name={id}
              value={option}
              checked={value.answer === option}
              onChange={(e) => onChange({ ...value, answer: e.target.value, details: '', comments: '' })}
              className="mr-2"
            />
            {option}
          </label>
        ))}
      </div>
      {value.answer === 'No' && (
        <div className="mt-3">
          <label className="label text-sm">
            Reasons for Late Opening (select all that apply) <span className="text-red-500">*</span>
          </label>
          <div className="space-y-2 mt-2">
            {lateOpeningReasons.map((reason) => (
              <label key={reason} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedReasons.includes(reason)}
                  onChange={(e) => handleReasonChange(reason, e.target.checked)}
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm text-gray-700">{reason}</span>
              </label>
            ))}
          </div>
          {isOtherSelected && (
            <div className="mt-3">
              <label className="label text-sm">
                Please specify other reason <span className="text-red-500">*</span>
              </label>
              <textarea
                placeholder="Please explain the other reason"
                className="input w-full h-20 resize-none"
                value={value.comments || ''}
                onChange={(e) => onChange({ ...value, comments: e.target.value })}
                required
              />
            </div>
          )}
          {selectedReasons.length === 0 && value.answer === 'No' && (
            <p className="text-red-500 text-sm mt-1">Please select at least one reason for late opening</p>
          )}
        </div>
      )}
      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

// Yes/No with Explanation for Yes
export const YesNoExplanationIfYesQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <div className="flex space-x-4 mb-2">
      {['Yes', 'No'].map((option) => (
        <label key={option} className="flex items-center">
          <input
            type="radio"
            name={id}
            value={option}
            checked={value.answer === option}
            onChange={(e) => onChange({ ...value, answer: e.target.value })}
            className="mr-2"
          />
          {option}
        </label>
      ))}
    </div>
    {value.answer === 'Yes' && (
      <textarea
        placeholder="Please explain (required)"
        className="input w-full h-20 resize-none"
        value={value.explanation || ''}
        onChange={(e) => onChange({ ...value, explanation: e.target.value })}
        required
      />
    )}
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Long Text Question
export const LongTextQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      placeholder="Provide any additional observations..."
      className="input w-full h-24 resize-none"
      maxLength={1000}
      value={value.comments || ''}
      onChange={(e) => onChange({ ...value, comments: e.target.value })}
    />
    <p className="text-xs text-gray-500 mt-1">
      {(value.comments || '').length}/1000 characters
    </p>
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Number Question
export const NumberQuestion: React.FC<BaseQuestionProps> = ({
  id, question, value, onChange, required, error
}) => (
  <div className="mb-4">
    <label className="label">
      {question} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="number"
      min="0"
      placeholder="Enter number..."
      className="input w-full"
      value={value.invalidBallotCount || ''}
      onChange={(e) => onChange({ ...value, invalidBallotCount: parseInt(e.target.value) || 0 })}
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

// Gender (male/female counts)
export const GenderQuestion: React.FC<{ id: string; question: string; value: QuestionAnswer; onChange: (value: QuestionAnswer) => void; required?: boolean; error?: string; }>
  = ({ id, question, value, onChange, required, error }) => {
    const male = typeof value.genderMale === 'number' ? value.genderMale : (value.genderMale ? Number(value.genderMale) : 0);
    const female = typeof value.genderFemale === 'number' ? value.genderFemale : (value.genderFemale ? Number(value.genderFemale) : 0);
    const total = (male || 0) + (female || 0);
    return (
      <div className="mb-4">
        <label className="label">
          {question} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="label text-sm">Male</label>
            <input
              type="number"
              min="0"
              className="input w-full"
              value={male}
              onChange={(e) => onChange({ ...value, genderMale: Math.max(0, parseInt(e.target.value || '0')) })}
            />
          </div>
          <div>
            <label className="label text-sm">Female</label>
            <input
              type="number"
              min="0"
              className="input w-full"
              value={female}
              onChange={(e) => onChange({ ...value, genderFemale: Math.max(0, parseInt(e.target.value || '0')) })}
            />
          </div>
          <div>
            <label className="label text-sm">Total</label>
            <input type="number" className="input w-full bg-gray-50" value={total} readOnly />
          </div>
        </div>
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  };

// Yes/No with Gender breakdown when Yes
export const YesNoGenderQuestion: React.FC<{ id: string; question: string; value: QuestionAnswer; onChange: (value: QuestionAnswer) => void; required?: boolean; error?: string; }>
  = ({ id, question, value, onChange, required, error }) => {
    const male = typeof value.genderMale === 'number' ? value.genderMale : (value.genderMale ? Number(value.genderMale) : 0);
    const female = typeof value.genderFemale === 'number' ? value.genderFemale : (value.genderFemale ? Number(value.genderFemale) : 0);
    const total = (male || 0) + (female || 0);
    return (
      <div className="mb-4">
        <label className="label">
          {question} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="flex space-x-4 mb-3">
          {['Yes', 'No'].map((option) => (
            <label key={option} className="flex items-center">
              <input
                type="radio"
                name={id}
                value={option}
                checked={value.answer === option}
                onChange={(e) => onChange({ ...value, answer: e.target.value, ...(e.target.value === 'No' ? { genderMale: 0, genderFemale: 0 } : {}) })}
                className="mr-2"
              />
              {option}
            </label>
          ))}
        </div>
        {value.answer === 'Yes' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label text-sm">Male</label>
              <input
                type="number"
                min="0"
                className="input w-full"
                value={male}
                onChange={(e) => onChange({ ...value, genderMale: Math.max(0, parseInt(e.target.value || '0')) })}
              />
            </div>
            <div>
              <label className="label text-sm">Female</label>
              <input
                type="number"
                min="0"
                className="input w-full"
                value={female}
                onChange={(e) => onChange({ ...value, genderFemale: Math.max(0, parseInt(e.target.value || '0')) })}
              />
            </div>
            <div>
              <label className="label text-sm">Total</label>
              <input type="number" className="input w-full bg-gray-50" value={total} readOnly />
            </div>
          </div>
        )}
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  };